import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import clsx from "clsx";
import { GdRichTextInline } from "@/components/ui/GdRichText";
import { Button } from "@/components/ui/Button";
import { ContextMenu, type ContextMenuState } from "@/components/ui/ContextMenu";
import {
  formatCurrency,
  formatQuantityInput,
  formatQuantityWithUnit,
  positionEpNetto,
  rabattEditorHint,
} from "@/lib/documentCalc";
import { buildPositionDisplayNumbers } from "@/pages/dokumente/documentPositionLayout";
import {
  absorbArtikelIntoLeistung,
  appendPositionsToMergedLeistung,
  applyLieferscheinDelivery,
  applyReorderWithTitelAssignment,
  countPositionsInTitelGroup,
  createBlockPosition,
  createArtikelPositionFromEinkauf,
  createGebuchteStundenPosition,
  createPositionFromHistorie,
  dissolveMergedPosition,
  duplicatePositionInList,
  getCardDropZone,
  getNachweisHinweis,
  getPositionMeta,
  groupPositionsUnderTitel,
  isMergeablePosition,
  isMergedContainerPosition,
  isNoOpInsertAt,
  mergePositionsIntoLeistung,
  isNachweisPosition,
  isTitelGroupBoundarySlot,
  findTitelGroupEndInsertIndex,
  movePositionIntoTitel,
  normalizePositionTyp,
  type PositionSelectionMode,
  POSITION_BLOCK_META,
  POSITION_DRAG_TYPE,
  POSITION_EINKAUF_ARTIKEL_DRAG_TYPE,
  POSITION_GEBUCHTE_STUNDEN_DRAG_TYPE,
  POSITION_INDEX_DRAG,
  type EinkaufArtikelDragPayload,
  type GebuchteStundenDragPayload,
  type NachweisStundenArtikelDefaults,
  positionBlockLabel,
  positionDisplayDescription,
  positionDisplayTitle,
  positionPositionsartBadgeLabel,
  positionPreisartBadgeLabel,
  positionTotals,
  readLieferscheinDelivery,
  resolveInsertTitelAssignment,
  resolvePositionsart,
  syncLeistungEinzelpreis,
  type PaletteBlockTyp,
  type ProvisionalPositionDraft,
  type CardDropZone,
  type EditorPosition,
} from "@/pages/dokumente/documentPositionModel";
import type {
  GdLohnart,
  GdPositionAttachmentMeta,
  AuftragEinkaufArtikel,
  DokumentTyp,
  HistorischeDokumentPosition,
} from "@/lib/dokumente";
import { fetchAuftragBookedHoursDetail } from "@/lib/bookedHours";
import {
  getAuftragEinkaufArtikel,
  getGdPositionAttachmentThumbUrl,
  getKundenStundenArtikel,
  listGdPositionAttachments,
  prefetchKundenStundenArtikel,
} from "@/lib/dokumente";
import { DEFAULT_ARTIKEL_AUFSCHLAG_PROZENT } from "@/pages/dokumente/documentPricing";
import {
  DocumentBuilderSidebar,
  POSITION_TEMPLATE_DRAG_TYPE,
} from "@/pages/dokumente/DocumentBuilderSidebar";
import { DocumentHistorischePositionenDialog } from "@/pages/dokumente/DocumentHistorischePositionenDialog";
import type { GdDocumentTemplateListItem } from "@/pages/dokumente/documentTemplateModel";
import "./dokumente.css";

const BLOCK_TYPES: PaletteBlockTyp[] = ["artikel", "leistung", "nachweisarbeiten", "titel", "text"];
/** Typische Kartenhöhe für Palette-/Vorlagen-Drags ohne gemessene Quelle. */
const DEFAULT_DRAG_PLACEHOLDER_HEIGHT = 88;
/** Verhindert Zappeln beim Wechsel benachbarter Drop-Slots. */
const SLOT_ACTIVATE_HYSTERESIS_MS = 70;

const EMPTY_BILLING_BADGE_MAP = new Map<string, "open" | "answered">();

interface DocumentPositionCanvasProps {
  positions: EditorPosition[];
  editable: boolean;
  currency: string;
  lohnarten: GdLohnart[];
  dokumentTyp: DokumentTyp;
  templates: GdDocumentTemplateListItem[];
  templatesLoading: boolean;
  applyingTemplateId: number | null;
  /** Vorlagen-Palette in der Sidebar anzeigen (Default: true). */
  templatesEnabled?: boolean;
  onChange: (positions: EditorPosition[]) => void;
  onOpenPosition: (localId: string, provisional?: ProvisionalPositionDraft) => void;
  onApplyTemplate: (templateId: number, replace: boolean, insertAt?: number) => void;
  onDeleteTemplate: (templateId: number) => void;
  onEditTemplate?: (templateId: number) => void;
  onReorderTemplates?: (orderedIds: number[]) => void;
  onSaveTemplate: () => void;
  /** Projekt für Einkaufsartikel-Palette (Rechnungen). */
  auftragId?: number | null;
  /** Lokale kunden.id für Standard-Stundenartikel bei Nachweisarbeiten. */
  kundenId?: number | null;
  /** Anzeigename des Dokument-Kunden für den Filter früherer Positionen. */
  kundeName?: string | null;
  /** Aktuelles Dokument – für historische Positionssuche und Anhänge. */
  docId?: number | null;
  /** Erhöhen, um Positionsbilder neu zu laden (nach Upload im Positionsdialog). */
  attachmentsRevision?: number;
  sidebarPlacement?: "inline" | "external";
  /** Rückfrage-Badge je Position (localId → open|answered). */
  billingBadgeByLocalId?: Map<string, "open" | "answered">;
}

function parseEinkaufDragPayload(e: React.DragEvent): EinkaufArtikelDragPayload | null {
  const raw = e.dataTransfer.getData(POSITION_EINKAUF_ARTIKEL_DRAG_TYPE);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as EinkaufArtikelDragPayload;
  } catch {
    return null;
  }
}

function parseGebuchteStundenDragPayload(e: React.DragEvent): GebuchteStundenDragPayload | null {
  const raw = e.dataTransfer.getData(POSITION_GEBUCHTE_STUNDEN_DRAG_TYPE);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as GebuchteStundenDragPayload;
  } catch {
    return null;
  }
}

/** Copy-Drags (Baustein/Vorlage/Einkauf/Stunden) vs. Move (bestehende Position neu ordnen). */
function isCopySourceDrag(
  e: React.DragEvent,
  paletteDragTyp: PaletteBlockTyp | null,
  paletteCopyDrag: boolean,
): boolean {
  if (paletteCopyDrag || paletteDragTyp != null) return true;
  const types = Array.from(e.dataTransfer?.types || []);
  if (types.includes(POSITION_EINKAUF_ARTIKEL_DRAG_TYPE)) return true;
  if (types.includes(POSITION_GEBUCHTE_STUNDEN_DRAG_TYPE)) return true;
  if (types.includes(POSITION_TEMPLATE_DRAG_TYPE)) return true;
  if (types.includes(POSITION_DRAG_TYPE) && !types.includes(POSITION_INDEX_DRAG)) return true;
  return false;
}

interface PositionCanvasContextValue {
  positions: EditorPosition[];
  editable: boolean;
  currency: string;
  lohnarten: GdLohnart[];
  dokumentTyp: DokumentTyp;
  templates: GdDocumentTemplateListItem[];
  templatesLoading: boolean;
  applyingTemplateId: number | null;
  templatesEnabled: boolean;
  onChange: (positions: EditorPosition[]) => void;
  onOpenPosition: (localId: string, provisional?: ProvisionalPositionDraft) => void;
  onApplyTemplate: (templateId: number, replace: boolean, insertAt?: number) => void;
  onDeleteTemplate: (templateId: number) => void;
  onEditTemplate?: (templateId: number) => void;
  onReorderTemplates?: (orderedIds: number[]) => void;
  onSaveTemplate: () => void;
  auftragId?: number | null;
  kundenId?: number | null;
  kundeName?: string | null;
  docId?: number | null;
  positionAttachments: GdPositionAttachmentMeta[];
  einkaufArtikel: AuftragEinkaufArtikel[];
  einkaufLoading: boolean;
  aufschlagProzent: number;
  onAufschlagChange: (value: number) => void;
  insertEinkaufArtikel: (item: AuftragEinkaufArtikel) => void;
  insertHistorischePosition: (item: HistorischeDokumentPosition) => void;
  gebuchteStundenTotal: number | null;
  gebuchteStundenLoading: boolean;
  insertGebuchteStunden: (totalHours?: number, targetIndex?: number) => void;
  dragIndex: number | null;
  dropIndex: number | null;
  dropTarget: "titel" | "leistung" | null;
  slotInsertAt: number | null;
  dragPlaceholderHeight: number;
  paletteOver: boolean;
  paletteDragTyp: PaletteBlockTyp | null;
  paletteCopyDrag: boolean;
  displayNumbers: (string | null)[];
  resetDrag: () => void;
  setPaletteOver: (active: boolean) => void;
  setPaletteDragTyp: (typ: PaletteBlockTyp) => void;
  setPaletteCopyDrag: (active: boolean) => void;
  insertBlock: (typ: PaletteBlockTyp, targetIndex?: number) => void;
  activateSlot: (insertAt: number) => void;
  beginPositionDrag: (index: number, height: number) => void;
  handleInsertAt: (insertAt: number, e: React.DragEvent) => void;
  handleCardDrop: (cardIndex: number, zone: CardDropZone, e: React.DragEvent) => void;
  handleCanvasDrop: (e: React.DragEvent) => void;
  handleCardDragOver: (cardIndex: number, zone: CardDropZone) => void;
  setDropTarget: (target: "titel" | "leistung" | null) => void;
  selectionMode: PositionSelectionMode | null;
  mergeExtendTargetLocalId: string | null;
  selectedLocalIds: Set<string>;
  contextMenu: ContextMenuState | null;
  startSelectionMode: (mode: PositionSelectionMode, initialLocalId?: string, mergeExtendTargetLocalId?: string) => void;
  cancelSelectionMode: () => void;
  confirmSelectionMode: () => void;
  toggleSelectedLocalId: (localId: string) => void;
  openContextMenu: (state: ContextMenuState) => void;
  closeContextMenu: () => void;
  dissolveMerged: (localId: string) => void;
  billingBadgeByLocalId: Map<string, "open" | "answered">;
}

const PositionCanvasContext = createContext<PositionCanvasContextValue | null>(null);

function usePositionCanvasContext(): PositionCanvasContextValue {
  const ctx = useContext(PositionCanvasContext);
  if (!ctx) {
    throw new Error("DocumentPositionCanvas-Komponenten müssen innerhalb von DocumentPositionCanvasProvider liegen.");
  }
  return ctx;
}

export function DocumentPositionCanvasProvider({
  children,
  ...props
}: DocumentPositionCanvasProps & { children: ReactNode }) {
  const value = usePositionCanvasState(props);
  return <PositionCanvasContext.Provider value={value}>{children}</PositionCanvasContext.Provider>;
}

function usePositionCanvasState({
  positions,
  editable,
  currency,
  lohnarten,
  dokumentTyp,
  templates,
  templatesLoading,
  applyingTemplateId,
  templatesEnabled = true,
  onChange,
  onOpenPosition,
  onApplyTemplate,
  onDeleteTemplate,
  onEditTemplate,
  onReorderTemplates,
  onSaveTemplate,
  auftragId,
  kundenId,
  kundeName = null,
  docId = null,
  attachmentsRevision = 0,
  billingBadgeByLocalId,
}: DocumentPositionCanvasProps): PositionCanvasContextValue {
  const billingBadges = billingBadgeByLocalId ?? EMPTY_BILLING_BADGE_MAP;
  const [einkaufArtikel, setEinkaufArtikel] = useState<AuftragEinkaufArtikel[]>([]);
  const [einkaufLoading, setEinkaufLoading] = useState(false);
  const [gebuchteStundenTotal, setGebuchteStundenTotal] = useState<number | null>(null);
  const [gebuchteStundenLoading, setGebuchteStundenLoading] = useState(false);
  const [aufschlagProzent, setAufschlagProzent] = useState(DEFAULT_ARTIKEL_AUFSCHLAG_PROZENT);
  const stundenArtikelCacheRef = useRef<{
    kundenId: number | null;
    auftragId: number | null;
    value: NachweisStundenArtikelDefaults | null;
  } | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<"titel" | "leistung" | null>(null);
  const [slotInsertAt, setSlotInsertAt] = useState<number | null>(null);
  const [dragPlaceholderHeight, setDragPlaceholderHeight] = useState(DEFAULT_DRAG_PLACEHOLDER_HEIGHT);
  const [paletteOver, setPaletteOver] = useState(false);
  const [paletteDragTyp, setPaletteDragTypState] = useState<PaletteBlockTyp | null>(null);
  const [paletteCopyDrag, setPaletteCopyDrag] = useState(false);
  const [selectionMode, setSelectionMode] = useState<PositionSelectionMode | null>(null);
  const [mergeExtendTargetLocalId, setMergeExtendTargetLocalId] = useState<string | null>(null);
  const [selectedLocalIds, setSelectedLocalIds] = useState<Set<string>>(() => new Set());
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [positionAttachments, setPositionAttachments] = useState<GdPositionAttachmentMeta[]>([]);
  const displayNumbers = useMemo(() => buildPositionDisplayNumbers(positions), [positions]);
  const slotInsertAtRef = useRef<number | null>(null);
  const pendingSlotRef = useRef<number | null>(null);
  const slotActivateTimerRef = useRef<number | null>(null);
  slotInsertAtRef.current = slotInsertAt;

  const clearSlotActivateTimer = useCallback(() => {
    if (slotActivateTimerRef.current != null) {
      window.clearTimeout(slotActivateTimerRef.current);
      slotActivateTimerRef.current = null;
    }
    pendingSlotRef.current = null;
  }, []);

  useEffect(() => {
    if (!docId) {
      setPositionAttachments([]);
      return;
    }
    let cancelled = false;
    void listGdPositionAttachments(docId)
      .then((list) => {
        if (!cancelled) setPositionAttachments(list);
      })
      .catch(() => {
        if (!cancelled) setPositionAttachments([]);
      });
    return () => {
      cancelled = true;
    };
  }, [docId, attachmentsRevision]);

  useEffect(() => {
    if (!editable || dokumentTyp !== "rechnung" || !auftragId || auftragId <= 0) {
      setEinkaufArtikel([]);
      return;
    }
    let cancelled = false;
    setEinkaufLoading(true);
    void getAuftragEinkaufArtikel(auftragId)
      .then((res) => {
        if (!cancelled) setEinkaufArtikel(res.items || []);
      })
      .catch(() => {
        if (!cancelled) setEinkaufArtikel([]);
      })
      .finally(() => {
        if (!cancelled) setEinkaufLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [auftragId, dokumentTyp, editable]);

  useEffect(() => {
    if (dokumentTyp !== "angebot" || !auftragId || auftragId <= 0) {
      setGebuchteStundenTotal(null);
      setGebuchteStundenLoading(false);
      return;
    }
    let cancelled = false;
    setGebuchteStundenLoading(true);
    void fetchAuftragBookedHoursDetail(auftragId)
      .then((detail) => {
        if (!cancelled) setGebuchteStundenTotal(detail.totalHours);
      })
      .catch(() => {
        if (!cancelled) setGebuchteStundenTotal(null);
      })
      .finally(() => {
        if (!cancelled) setGebuchteStundenLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [auftragId, dokumentTyp]);

  const resetDrag = useCallback(() => {
    clearSlotActivateTimer();
    setDragIndex(null);
    setDropIndex(null);
    setDropTarget(null);
    setSlotInsertAt(null);
    setDragPlaceholderHeight(DEFAULT_DRAG_PLACEHOLDER_HEIGHT);
    setPaletteOver(false);
    setPaletteDragTypState(null);
    setPaletteCopyDrag(false);
  }, [clearSlotActivateTimer]);

  const cancelSelectionMode = useCallback(() => {
    setSelectionMode(null);
    setMergeExtendTargetLocalId(null);
    setSelectedLocalIds(new Set());
  }, []);

  const startSelectionMode = useCallback(
    (mode: PositionSelectionMode, initialLocalId?: string, extendTargetLocalId?: string) => {
      setSelectionMode(mode);
      setMergeExtendTargetLocalId(mode === "merge-extend" ? extendTargetLocalId ?? null : null);
      setSelectedLocalIds(initialLocalId ? new Set([initialLocalId]) : new Set());
      setContextMenu(null);
      resetDrag();
    },
    [resetDrag],
  );

  const toggleSelectedLocalId = useCallback((localId: string) => {
    setSelectedLocalIds((current) => {
      const next = new Set(current);
      if (next.has(localId)) next.delete(localId);
      else next.add(localId);
      return next;
    });
  }, []);

  const confirmSelectionMode = useCallback(() => {
    if (!selectionMode) return;
    const ids = [...selectedLocalIds];
    if (selectionMode === "merge") {
      if (ids.length < 2) return;
      onChange(mergePositionsIntoLeistung(positions, ids));
    } else if (selectionMode === "merge-extend") {
      if (!mergeExtendTargetLocalId || ids.length < 1) return;
      onChange(appendPositionsToMergedLeistung(positions, mergeExtendTargetLocalId, ids));
    } else if (selectionMode === "titel") {
      if (ids.length < 1) return;
      onChange(groupPositionsUnderTitel(positions, ids));
    }
    cancelSelectionMode();
  }, [
    cancelSelectionMode,
    mergeExtendTargetLocalId,
    onChange,
    positions,
    selectedLocalIds,
    selectionMode,
  ]);

  const openContextMenu = useCallback((state: ContextMenuState) => {
    setContextMenu(state);
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const dissolveMerged = useCallback(
    (localId: string) => {
      onChange(dissolveMergedPosition(positions, localId));
      closeContextMenu();
    },
    [closeContextMenu, onChange, positions],
  );

  const setPaletteDragTyp = useCallback((typ: PaletteBlockTyp) => {
    setPaletteDragTypState(typ);
    setPaletteCopyDrag(true);
    setDragPlaceholderHeight(DEFAULT_DRAG_PLACEHOLDER_HEIGHT);
  }, []);

  const resolvePaletteTyp = useCallback(
    (e: React.DragEvent): PaletteBlockTyp | null => {
      if (paletteDragTyp && BLOCK_TYPES.includes(paletteDragTyp)) return paletteDragTyp;
      const fromData = e.dataTransfer.getData(POSITION_DRAG_TYPE) as PaletteBlockTyp;
      return BLOCK_TYPES.includes(fromData) ? fromData : null;
    },
    [paletteDragTyp],
  );

  const beginPositionDrag = useCallback((index: number, height: number) => {
    clearSlotActivateTimer();
    setDragIndex(index);
    setDropTarget(null);
    setDropIndex(null);
    setSlotInsertAt(null);
    setDragPlaceholderHeight(
      Number.isFinite(height) && height > 0 ? Math.round(height) : DEFAULT_DRAG_PLACEHOLDER_HEIGHT,
    );
  }, [clearSlotActivateTimer]);

  const activateSlot = useCallback(
    (insertAt: number) => {
      if (dragIndex != null && isNoOpInsertAt(dragIndex, insertAt)) {
        return;
      }
      if (slotInsertAtRef.current === insertAt) {
        if (dropTarget != null || dropIndex != null) {
          setDropIndex(null);
          setDropTarget(null);
        }
        return;
      }

      pendingSlotRef.current = insertAt;
      if (slotActivateTimerRef.current != null) {
        window.clearTimeout(slotActivateTimerRef.current);
        slotActivateTimerRef.current = null;
      }

      const apply = () => {
        if (pendingSlotRef.current !== insertAt) return;
        setSlotInsertAt(insertAt);
        setDropIndex(null);
        setDropTarget(null);
        slotActivateTimerRef.current = null;
      };

      // Erster Treffer sofort, Wechsel benachbarter Slots mit Hysterese gegen Zappeln.
      if (slotInsertAtRef.current == null) {
        apply();
        return;
      }
      slotActivateTimerRef.current = window.setTimeout(apply, SLOT_ACTIVATE_HYSTERESIS_MS);
    },
    [dragIndex, dropIndex, dropTarget],
  );

  const resolveStundenArtikel = useCallback(async (): Promise<NachweisStundenArtikelDefaults | null> => {
    const keyKunden = kundenId ?? null;
    const keyAuftrag = auftragId ?? null;
    const cached = stundenArtikelCacheRef.current;
    if (cached && cached.kundenId === keyKunden && cached.auftragId === keyAuftrag) {
      return cached.value;
    }
    try {
      const raw = await getKundenStundenArtikel(keyKunden, { auftragId: keyAuftrag });
      const value: NachweisStundenArtikelDefaults | null =
        raw.artikel_id != null || (raw.hero_nr || "").trim()
          ? {
              artikel_id: raw.artikel_id ?? null,
              hero_nr: raw.hero_nr ?? null,
              bezeichnung: raw.bezeichnung ?? null,
              einheit: raw.einheit ?? "Std.",
              einzelpreis_netto: raw.einzelpreis_netto ?? null,
              rabatt_prozent: raw.rabatt_prozent ?? null,
            }
          : null;
      stundenArtikelCacheRef.current = { kundenId: keyKunden, auftragId: keyAuftrag, value };
      return value;
    } catch {
      stundenArtikelCacheRef.current = { kundenId: keyKunden, auftragId: keyAuftrag, value: null };
      return null;
    }
  }, [kundenId, auftragId]);

  // Stunden-Artikel im Hintergrund vorladen (Nachweisarbeiten ohne Wartezeit)
  useEffect(() => {
    prefetchKundenStundenArtikel(kundenId ?? null, { auftragId: auftragId ?? null });
    void resolveStundenArtikel();
  }, [kundenId, auftragId, resolveStundenArtikel]);

  const stageNewPosition = useCallback(
    (block: EditorPosition, insertAt: number, titelAssignment: string | null | undefined) => {
      onOpenPosition(block.localId, { position: block, insertAt, titelAssignment });
    },
    [onOpenPosition],
  );

  const insertBlock = useCallback(
    (typ: PaletteBlockTyp, targetIndex?: number) => {
      void (async () => {
        const snapshot = positions;
        const index = targetIndex ?? snapshot.length;
        const stundenArtikel =
          typ === "nachweisarbeiten" ? await resolveStundenArtikel() : null;
        const next = createBlockPosition(typ, snapshot.length, {
          lohnarten: typ === "leistung" ? lohnarten : undefined,
          stundenArtikel,
        });
        const titelAssignment = resolveInsertTitelAssignment(snapshot, index);
        stageNewPosition(next, index, titelAssignment);
      })();
    },
    [lohnarten, positions, resolveStundenArtikel, stageNewPosition],
  );

  const insertEinkaufArtikel = useCallback(
    (item: AuftragEinkaufArtikel) => {
      const index = positions.length;
      const block = createArtikelPositionFromEinkauf(item, positions.length, aufschlagProzent);
      const titelAssignment = resolveInsertTitelAssignment(positions, index);
      stageNewPosition(block, index, titelAssignment);
    },
    [aufschlagProzent, positions, stageNewPosition],
  );

  const insertHistorischePosition = useCallback(
    (item: HistorischeDokumentPosition) => {
      const index = positions.length;
      const block = createPositionFromHistorie(item, positions.length);
      const titelAssignment = resolveInsertTitelAssignment(positions, index);
      stageNewPosition(block, index, titelAssignment);
    },
    [positions, stageNewPosition],
  );

  const insertGebuchteStunden = useCallback(
    (totalHours?: number, targetIndex?: number) => {
      void (async () => {
        const hours =
          totalHours != null && Number.isFinite(totalHours) && totalHours > 0
            ? totalHours
            : gebuchteStundenTotal;
        if (hours == null || !(hours > 0)) return;
        const snapshot = positions;
        const index = targetIndex ?? snapshot.length;
        const stundenArtikel = await resolveStundenArtikel();
        const block = createGebuchteStundenPosition(snapshot.length, hours, stundenArtikel);
        const titelAssignment = resolveInsertTitelAssignment(snapshot, index);
        stageNewPosition(block, index, titelAssignment);
      })();
    },
    [gebuchteStundenTotal, positions, resolveStundenArtikel, stageNewPosition],
  );

  const handleInsertAt = useCallback(
    (insertAt: number, e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const paletteTyp = resolvePaletteTyp(e);
      const fromIndexRaw = e.dataTransfer.getData(POSITION_INDEX_DRAG);
      const fromIndex = dragIndex ?? (fromIndexRaw ? Number(fromIndexRaw) : NaN);
      const pointer = {
        clientX: e.clientX,
        clientY: e.clientY,
        rect: e.currentTarget.getBoundingClientRect(),
      };
      const titelAssignment = resolveInsertTitelAssignment(positions, insertAt, pointer);

      const einkaufPayload = parseEinkaufDragPayload(e);
      if (einkaufPayload && dragIndex === null) {
        const block = createArtikelPositionFromEinkauf(
          einkaufPayload.item,
          positions.length,
          einkaufPayload.aufschlagProzent,
        );
        stageNewPosition(block, insertAt, titelAssignment);
        resetDrag();
        return;
      }

      const gebuchtePayload = parseGebuchteStundenDragPayload(e);
      if (gebuchtePayload && dragIndex === null) {
        insertGebuchteStunden(gebuchtePayload.totalHours, insertAt);
        resetDrag();
        return;
      }

      const templateIdRaw = e.dataTransfer.getData(POSITION_TEMPLATE_DRAG_TYPE);
      if (templateIdRaw && dragIndex === null) {
        const templateId = Number(templateIdRaw);
        if (Number.isFinite(templateId)) {
          onApplyTemplate(templateId, false, insertAt);
          resetDrag();
          return;
        }
      }

      if (paletteTyp && dragIndex === null) {
        void (async () => {
          const stundenArtikel =
            paletteTyp === "nachweisarbeiten" ? await resolveStundenArtikel() : null;
          const block = createBlockPosition(paletteTyp, positions.length, {
            lohnarten: paletteTyp === "leistung" ? lohnarten : undefined,
            stundenArtikel,
          });
          stageNewPosition(block, insertAt, titelAssignment);
        })();
        resetDrag();
        return;
      }

      if (!Number.isFinite(fromIndex)) {
        resetDrag();
        return;
      }

      onChange(applyReorderWithTitelAssignment(positions, fromIndex, insertAt, titelAssignment));
      resetDrag();
    },
    [
      dragIndex,
      insertGebuchteStunden,
      lohnarten,
      onApplyTemplate,
      onChange,
      positions,
      resetDrag,
      resolvePaletteTyp,
      resolveStundenArtikel,
      stageNewPosition,
    ],
  );

  const resolveCardInsertAt = useCallback(
    (cardIndex: number, zone: CardDropZone): { insertAt: number; titelAssignment: string | null | undefined } => {
      const targetRow = positions[cardIndex];
      const targetTyp = normalizePositionTyp(targetRow?.position_typ);
      if (zone === "into" && targetTyp === "titel" && targetRow) {
        const insertAt = findTitelGroupEndInsertIndex(positions, targetRow.localId);
        return { insertAt, titelAssignment: targetRow.localId };
      }
      const insertAt = zone === "before" ? cardIndex : cardIndex + 1;
      return { insertAt, titelAssignment: resolveInsertTitelAssignment(positions, insertAt) };
    },
    [positions],
  );

  const handleCardDrop = useCallback(
    (cardIndex: number, zone: CardDropZone, e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const paletteTyp = resolvePaletteTyp(e);
      const einkaufPayload = parseEinkaufDragPayload(e);
      if (einkaufPayload && dragIndex === null) {
        const { insertAt, titelAssignment } = resolveCardInsertAt(cardIndex, zone);
        const block = createArtikelPositionFromEinkauf(
          einkaufPayload.item,
          positions.length,
          einkaufPayload.aufschlagProzent,
        );
        stageNewPosition(block, insertAt, titelAssignment);
        resetDrag();
        return;
      }
      const gebuchtePayload = parseGebuchteStundenDragPayload(e);
      if (gebuchtePayload && dragIndex === null) {
        const { insertAt } = resolveCardInsertAt(cardIndex, zone);
        insertGebuchteStunden(gebuchtePayload.totalHours, insertAt);
        resetDrag();
        return;
      }
      const templateIdRaw = e.dataTransfer.getData(POSITION_TEMPLATE_DRAG_TYPE);
      if (templateIdRaw && dragIndex === null) {
        const templateId = Number(templateIdRaw);
        if (Number.isFinite(templateId)) {
          const { insertAt } = resolveCardInsertAt(cardIndex, zone);
          onApplyTemplate(templateId, false, insertAt);
          resetDrag();
          return;
        }
      }
      if (paletteTyp && dragIndex === null) {
        const { insertAt, titelAssignment } = resolveCardInsertAt(cardIndex, zone);
        void (async () => {
          const stundenArtikel =
            paletteTyp === "nachweisarbeiten" ? await resolveStundenArtikel() : null;
          const block = createBlockPosition(paletteTyp, positions.length, {
            lohnarten: paletteTyp === "leistung" ? lohnarten : undefined,
            stundenArtikel,
          });
          stageNewPosition(block, insertAt, titelAssignment);
        })();
        resetDrag();
        return;
      }

      const fromIndexRaw = e.dataTransfer.getData(POSITION_INDEX_DRAG);
      const fromIndex = dragIndex ?? (fromIndexRaw ? Number(fromIndexRaw) : NaN);
      if (!Number.isFinite(fromIndex)) {
        resetDrag();
        return;
      }

      const targetRow = positions[cardIndex];
      const draggedRow = positions[fromIndex];
      const targetTyp = normalizePositionTyp(targetRow?.position_typ);

      if (
        zone === "into" &&
        targetTyp === "titel" &&
        draggedRow &&
        targetRow &&
        normalizePositionTyp(draggedRow.position_typ) !== "titel"
      ) {
        onChange(movePositionIntoTitel(positions, fromIndex, targetRow.localId));
        resetDrag();
        return;
      }

      if (
        zone === "into" &&
        targetTyp === "leistung" &&
        draggedRow &&
        normalizePositionTyp(draggedRow.position_typ) === "artikel"
      ) {
        onChange(absorbArtikelIntoLeistung(positions, fromIndex, cardIndex));
        resetDrag();
        return;
      }

      if (fromIndex === cardIndex && zone !== "into") {
        resetDrag();
        return;
      }

      const { insertAt, titelAssignment } = resolveCardInsertAt(cardIndex, zone);
      onChange(applyReorderWithTitelAssignment(positions, fromIndex, insertAt, titelAssignment));
      resetDrag();
    },
    [
      dragIndex,
      insertGebuchteStunden,
      lohnarten,
      onApplyTemplate,
      onChange,
      positions,
      resetDrag,
      resolveCardInsertAt,
      resolvePaletteTyp,
      resolveStundenArtikel,
      stageNewPosition,
    ],
  );

  const handleCanvasDrop = useCallback(
    (e: React.DragEvent) => {
      if (!editable) return;
      e.preventDefault();
      const einkaufPayload = parseEinkaufDragPayload(e);
      if (einkaufPayload && dragIndex === null) {
        const index = positions.length;
        const block = createArtikelPositionFromEinkauf(
          einkaufPayload.item,
          positions.length,
          einkaufPayload.aufschlagProzent,
        );
        const titelAssignment = resolveInsertTitelAssignment(positions, index);
        stageNewPosition(block, index, titelAssignment);
        resetDrag();
        return;
      }
      const gebuchtePayload = parseGebuchteStundenDragPayload(e);
      if (gebuchtePayload && dragIndex === null) {
        insertGebuchteStunden(gebuchtePayload.totalHours);
        resetDrag();
        return;
      }
      const templateIdRaw = e.dataTransfer.getData(POSITION_TEMPLATE_DRAG_TYPE);
      if (templateIdRaw && dragIndex === null) {
        const templateId = Number(templateIdRaw);
        if (Number.isFinite(templateId)) {
          const insertAt = slotInsertAt != null ? slotInsertAt : positions.length;
          onApplyTemplate(templateId, false, insertAt);
          resetDrag();
          return;
        }
      }
      const paletteTyp = resolvePaletteTyp(e);
      if (paletteTyp && dragIndex === null) {
        insertBlock(paletteTyp);
        resetDrag();
        return;
      }
      if (dragIndex !== null && slotInsertAt != null) {
        const pointer = {
          clientX: e.clientX,
          clientY: e.clientY,
          rect: e.currentTarget.getBoundingClientRect(),
        };
        const titelAssignment = resolveInsertTitelAssignment(positions, slotInsertAt, pointer);
        onChange(applyReorderWithTitelAssignment(positions, dragIndex, slotInsertAt, titelAssignment));
      }
      resetDrag();
    },
    [
      dragIndex,
      insertBlock,
      insertGebuchteStunden,
      onApplyTemplate,
      onChange,
      positions,
      editable,
      resetDrag,
      resolvePaletteTyp,
      slotInsertAt,
      stageNewPosition,
    ],
  );

  const handleCardDragOver = useCallback(
    (cardIndex: number, zone: CardDropZone) => {
      if (zone === "into") {
        const dragged = dragIndex != null ? positions[dragIndex] : null;
        const target = positions[cardIndex];
        const targetTyp = target ? normalizePositionTyp(target.position_typ) : null;
        const markInto = (target: "titel" | "leistung") => {
          clearSlotActivateTimer();
          setDropTarget(target);
          setDropIndex(cardIndex);
          setSlotInsertAt(null);
        };
        // Palette/Copy-Drag: Titel-Mitte als Gruppenziel markieren.
        if (dragIndex == null && targetTyp === "titel") {
          markInto("titel");
          return;
        }
        if (
          dragged &&
          targetTyp === "titel" &&
          normalizePositionTyp(dragged.position_typ) !== "titel"
        ) {
          markInto("titel");
          return;
        }
        if (
          dragged &&
          targetTyp === "leistung" &&
          normalizePositionTyp(dragged.position_typ) === "artikel"
        ) {
          markInto("leistung");
          return;
        }
      }

      const insertAt = zone === "before" ? cardIndex : cardIndex + 1;
      if (dragIndex != null && isNoOpInsertAt(dragIndex, insertAt)) {
        return;
      }
      activateSlot(insertAt);
    },
    [activateSlot, clearSlotActivateTimer, dragIndex, positions],
  );

  return {
    positions,
    editable,
    currency,
    lohnarten,
    dokumentTyp,
    templates,
    templatesLoading,
    applyingTemplateId,
    templatesEnabled,
    onChange,
    onOpenPosition,
    onApplyTemplate,
    onDeleteTemplate,
    onEditTemplate,
    onReorderTemplates,
    onSaveTemplate,
    auftragId,
    kundenId,
    kundeName,
    docId,
    positionAttachments,
    einkaufArtikel,
    einkaufLoading,
    aufschlagProzent,
    onAufschlagChange: setAufschlagProzent,
    insertEinkaufArtikel,
    insertHistorischePosition,
    gebuchteStundenTotal,
    gebuchteStundenLoading,
    insertGebuchteStunden,
    dragIndex,
    dropIndex,
    dropTarget,
    slotInsertAt,
    dragPlaceholderHeight,
    paletteOver,
    paletteDragTyp,
    paletteCopyDrag,
    displayNumbers,
    resetDrag,
    setPaletteOver,
    setPaletteDragTyp,
    setPaletteCopyDrag,
    insertBlock,
    activateSlot,
    beginPositionDrag,
    handleInsertAt,
    handleCardDrop,
    handleCanvasDrop,
    handleCardDragOver,
    setDropTarget,
    selectionMode,
    mergeExtendTargetLocalId,
    selectedLocalIds,
    contextMenu,
    startSelectionMode,
    cancelSelectionMode,
    confirmSelectionMode,
    toggleSelectedLocalId,
    openContextMenu,
    closeContextMenu,
    dissolveMerged,
    billingBadgeByLocalId: billingBadges,
  };
}

function PositionDropSlot({
  insertAt,
  active,
  isPaletteDrag,
  dragActive,
  titelBoundary,
  placeholderHeight,
  onActivate,
  onDropAt,
}: {
  insertAt: number;
  active: boolean;
  isPaletteDrag: boolean;
  dragActive: boolean;
  titelBoundary: boolean;
  placeholderHeight: number;
  onActivate: (insertAt: number) => void;
  onDropAt: (insertAt: number, e: React.DragEvent) => void;
}) {
  return (
    <div
      className={clsx(
        "gd-position-drop-slot",
        dragActive && "is-drag-ready",
        active && "is-active",
        titelBoundary && active && "is-titel-boundary",
      )}
      style={
        active
          ? ({
              "--gd-drop-placeholder-height": `${placeholderHeight}px`,
            } as React.CSSProperties)
          : undefined
      }
      title={
        titelBoundary
          ? "Oben rechts: in Titelgruppe · Unten links: außerhalb"
          : undefined
      }
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = isPaletteDrag ? "copy" : "move";
        onActivate(insertAt);
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onDropAt(insertAt, e);
      }}
    >
      <span className="gd-position-drop-slot__placeholder" aria-hidden={!active}>
        {titelBoundary ? "In Titelgruppe / außerhalb" : "Hier einfügen"}
      </span>
    </div>
  );
}

function PositionCard({
  row,
  index,
  currency,
  editable,
  positions,
  dragIndex,
  dropIndex,
  dropTarget,
  grouped,
  groupCount,
  displayNumber,
  selectionMode,
  selected,
  selectable,
  isCopyDrag,
  imageUrls,
  isLieferschein,
  billingBadge,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onOpen,
  onRemove,
  onContextMenu,
  onToggleSelect,
  onDeliveryChange,
}: {
  row: EditorPosition;
  index: number;
  currency: string;
  editable: boolean;
  positions: EditorPosition[];
  dragIndex: number | null;
  dropIndex: number | null;
  dropTarget: "titel" | "leistung" | null;
  grouped: boolean;
  groupCount?: number;
  displayNumber: string | null;
  selectionMode: PositionSelectionMode | null;
  selected: boolean;
  selectable: boolean;
  isCopyDrag: boolean;
  imageUrls: string[];
  isLieferschein: boolean;
  billingBadge?: "open" | "answered" | null;
  onDragStart: (index: number, height: number) => void;
  onDragEnd: () => void;
  onDragOver: (index: number, zone: CardDropZone) => void;
  onDrop: (index: number, zone: CardDropZone, e: React.DragEvent) => void;
  onOpen: () => void;
  onRemove: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onToggleSelect: () => void;
  onDeliveryChange: (
    localId: string,
    update: { geliefert?: number; rueckstandEntfernt?: boolean },
  ) => void;
}) {
  const typ = normalizePositionTyp(row.position_typ);
  const isNachweis = isNachweisPosition(row);
  const blockLabel = positionBlockLabel(row);
  const blockIcon = isNachweis
    ? POSITION_BLOCK_META.nachweisarbeiten.icon
    : POSITION_BLOCK_META[typ].icon;
  const pricedRow = typ === "leistung" ? syncLeistungEinzelpreis(row) : row;
  const totals = positionTotals(pricedRow);
  const priced = typ !== "titel" && typ !== "text";
  const rabattHint = priced ? rabattEditorHint(pricedRow, currency) : null;
  const title = positionDisplayTitle(row);
  const hasDescription = Boolean((row.beschreibung || "").trim());
  const description =
    positionDisplayDescription(row) ||
    (typ === "text" && !(row.bezeichnung || "").trim() ? "Kein Text" : "");
  const hasInterneNotiz = Boolean((getPositionMeta(row).interne_notiz || "").trim());
  const mergedChildren = getPositionMeta(row).merged_children;
  const isMerged = isMergedContainerPosition(row);
  const preisartBadge = positionPreisartBadgeLabel(row);
  const positionsartBadge = positionPositionsartBadgeLabel(row);
  const nachweisHinweis = isNachweis ? getNachweisHinweis(row) : "";
  const delivery = isLieferschein && priced ? readLieferscheinDelivery(pricedRow) : null;
  const mengeLabel = priced
    ? delivery
      ? formatQuantityWithUnit(delivery.beauftragte_menge, pricedRow.einheit)
      : formatQuantityWithUnit(pricedRow.menge, pricedRow.einheit)
    : "";
  const inSelectionMode = selectionMode != null;
  const canDrag = editable && !inSelectionMode;
  const [dragBlocked, setDragBlocked] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);

  function isInteractiveDragTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    // Nur echte Eingabefelder – sonst lässt sich die Zeile überall greifen.
    return Boolean(
      target.closest(
        "input, textarea, select, option, [contenteditable=''], [contenteditable='true'], [data-no-drag]",
      ),
    );
  }

  function blockCardDrag() {
    setDragBlocked(true);
    const card = cardRef.current;
    if (card) card.draggable = false;
  }

  function unblockCardDragSoon() {
    window.requestAnimationFrame(() => {
      const active = document.activeElement;
      if (
        active instanceof HTMLElement &&
        cardRef.current?.contains(active) &&
        isInteractiveDragTarget(active)
      ) {
        blockCardDrag();
        return;
      }
      setDragBlocked(false);
      const card = cardRef.current;
      if (card) card.draggable = canDrag;
    });
  }

  return (
    <div
      ref={cardRef}
      className={clsx(
        "gd-position-card",
        isLieferschein && "gd-position-card--lieferschein",
        `gd-position-card--${typ}`,
        grouped && "gd-position-card--grouped",
        !editable && "gd-position-card--readonly",
        selected && "is-selected",
        inSelectionMode && selectable && "is-selectable",
        dragIndex === index && "is-dragging",
        dropTarget === "titel" && dropIndex === index && typ === "titel" && "is-titel-drop-target",
        dropTarget === "leistung" && dropIndex === index && typ === "leistung" && "is-leistung-drop-target",
      )}
      draggable={canDrag && !dragBlocked}
      onClick={() => {
        if (editable || inSelectionMode) return;
        onOpen();
      }}
      onContextMenu={onContextMenu}
      onMouseDownCapture={(e) => {
        if (!canDrag) return;
        if (isInteractiveDragTarget(e.target)) {
          blockCardDrag();
          return;
        }
        setDragBlocked(false);
        const card = cardRef.current;
        if (card) card.draggable = true;
      }}
      onFocusCapture={(e) => {
        if (!canDrag) return;
        if (isInteractiveDragTarget(e.target)) {
          blockCardDrag();
        }
      }}
      onBlurCapture={() => {
        if (!canDrag) return;
        unblockCardDragSoon();
      }}
      onMouseUp={() => {
        if (!canDrag) return;
        unblockCardDragSoon();
      }}
      onDragStart={(e) => {
        if (!canDrag || dragBlocked || isInteractiveDragTarget(e.target)) {
          e.preventDefault();
          blockCardDrag();
          return;
        }
        const height = e.currentTarget.getBoundingClientRect().height;
        onDragStart(index, height);
        e.dataTransfer.setData(POSITION_DRAG_TYPE, typ);
        e.dataTransfer.setData(POSITION_INDEX_DRAG, String(index));
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragEnd={() => {
        onDragEnd();
        unblockCardDragSoon();
      }}
      onDragOver={(e) => {
        if (!editable) return;
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        if (isCopyDrag) {
          e.dataTransfer.dropEffect = "copy";
          const allowIntoTitel = typ === "titel";
          const zone = getCardDropZone(e.clientY, rect, allowIntoTitel);
          onDragOver(index, zone);
          return;
        }
        const fromIndexRaw = e.dataTransfer.getData(POSITION_INDEX_DRAG);
        const fromIndex = dragIndex ?? (fromIndexRaw ? Number(fromIndexRaw) : NaN);
        if (!Number.isFinite(fromIndex) || fromIndex === index) return;

        const dragged = positions[fromIndex];
        const allowIntoTitel =
          typ === "titel" && dragged != null && normalizePositionTyp(dragged.position_typ) !== "titel";
        const allowIntoLeistung =
          typ === "leistung" && dragged != null && normalizePositionTyp(dragged.position_typ) === "artikel";
        const zone = getCardDropZone(e.clientY, rect, allowIntoTitel || allowIntoLeistung);

        e.dataTransfer.dropEffect = "move";
        onDragOver(index, zone);
      }}
      onDrop={(e) => {
        if (!editable) return;
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const fromIndexRaw = e.dataTransfer.getData(POSITION_INDEX_DRAG);
        const fromIndex = dragIndex ?? (fromIndexRaw ? Number(fromIndexRaw) : NaN);
        const dragged = Number.isFinite(fromIndex) ? positions[fromIndex] : undefined;
        const allowIntoTitel =
          typ === "titel" &&
          (isCopyDrag ||
            (dragged != null && normalizePositionTyp(dragged.position_typ) !== "titel"));
        const allowIntoLeistung =
          !isCopyDrag &&
          typ === "leistung" &&
          dragged != null &&
          normalizePositionTyp(dragged.position_typ) === "artikel";
        const zone = getCardDropZone(e.clientY, rect, allowIntoTitel || allowIntoLeistung);
        onDrop(index, zone, e);
      }}
    >
      {nachweisHinweis ? (
        <div className="gd-position-card__nachweis-hinweis">{nachweisHinweis}</div>
      ) : null}
      <div className="gd-position-card__handle" aria-hidden>
        ⋮⋮
      </div>
      <div className="gd-position-card__pos">{displayNumber ?? ""}</div>
      <div className="gd-position-card__menge">{mengeLabel}</div>
      {isLieferschein ? (
        <div className="gd-position-card__geliefert" data-no-drag>
          {priced && delivery ? (
            editable && !inSelectionMode ? (
              <input
                type="number"
                min={0}
                step="any"
                draggable={false}
                className="gd-position-card__geliefert-input"
                value={formatQuantityInput(delivery.geliefert_menge)}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onDragStart={(e) => e.preventDefault()}
                onChange={(e) =>
                  onDeliveryChange(row.localId, { geliefert: Number(e.target.value) })
                }
                aria-label={`Geliefert: ${title}`}
              />
            ) : (
              formatQuantityWithUnit(delivery.geliefert_menge, pricedRow.einheit)
            )
          ) : null}
        </div>
      ) : null}
      {isLieferschein ? (
        <div className="gd-position-card__rueckstand">
          {priced && delivery ? (
            <>
              <span>
                {delivery.rueckstand_menge > 0.0005
                  ? formatQuantityWithUnit(delivery.rueckstand_menge, pricedRow.einheit)
                  : "—"}
              </span>
              {editable && !inSelectionMode && delivery.rueckstand_menge > 0.0005 ? (
                <button
                  type="button"
                  className="gd-position-card__rueckstand-clear"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeliveryChange(row.localId, { rueckstandEntfernt: true });
                  }}
                >
                  Löschen
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
      <button
        type="button"
        className="gd-position-card__bezeichnung text-left"
        title={
          !editable && !inSelectionMode ? "Artikeldetails und Kalkulation ansehen" : undefined
        }
        onClick={() => {
          if (inSelectionMode && selectable) {
            onToggleSelect();
            return;
          }
          onOpen();
        }}
      >
        <div className="gd-position-card__type">
          {blockIcon} {blockLabel}
          {preisartBadge ? (
            <span
              className={clsx(
                "gd-position-card__badge",
                preisartBadge === "Nachweis"
                  ? "gd-position-card__badge--nachweis"
                  : "gd-position-card__badge--festpreis",
              )}
              title="Nur intern"
            >
              {preisartBadge}
            </span>
          ) : null}
          {positionsartBadge ? (
            <span
              className={clsx(
                "gd-position-card__badge",
                resolvePositionsart(row) === "optional"
                  ? "gd-position-card__badge--optional"
                  : "gd-position-card__badge--alternativ",
              )}
              title={
                resolvePositionsart(row) === "optional"
                  ? "Optionale Position – in der Gesamtsumme enthalten"
                  : "Alternative Position – nicht in der Gesamtsumme"
              }
            >
              {positionsartBadge}
            </span>
          ) : null}
          {typ === "titel" && groupCount != null && groupCount > 0 ? (
            <span className="gd-position-card__badge">{groupCount} Positionen</span>
          ) : null}
          {isMerged && mergedChildren?.length ? (
            <span className="gd-position-card__badge">{mergedChildren.length} Unterpositionen</span>
          ) : null}
          {billingBadge ? (
            <span
              className={clsx(
                "gd-position-card__badge",
                billingBadge === "open"
                  ? "gd-position-card__badge--rueckfrage-open"
                  : "gd-position-card__badge--rueckfrage-answered",
              )}
              title={
                billingBadge === "open"
                  ? "Offene Abrechnungs-Rückfrage"
                  : "Beantwortete Abrechnungs-Rückfrage"
              }
            >
              Rückfrage
            </span>
          ) : null}
        </div>
        <div className="gd-position-card__title">{title}</div>
        <div className={clsx("gd-position-card__desc", !hasDescription && "is-empty")}>
          <GdRichTextInline text={description} />
        </div>
        {imageUrls.length ? (
          <div className="gd-position-card__images" aria-label="Positionsbilder">
            {imageUrls.slice(0, 4).map((url) => (
              <img
                key={url}
                src={url}
                alt=""
                crossOrigin="use-credentials"
                className="gd-position-card__image"
              />
            ))}
            {imageUrls.length > 4 ? (
              <span className="gd-position-card__images-more">+{imageUrls.length - 4}</span>
            ) : null}
          </div>
        ) : null}
        {hasInterneNotiz ? (
          <div className="gd-position-card__intern">Interne Notiz vorhanden</div>
        ) : null}
        {typ === "leistung" && editable ? (
          <div className="gd-position-card__hint">
            {dropTarget === "leistung" && dropIndex === index
              ? "Artikel hier ablegen"
              : isNachweis
                ? "Stunden-Artikel per Drag & Drop hinzufügen"
                : "Artikel per Drag & Drop hinzufügen"}
          </div>
        ) : null}
        {typ === "titel" && editable ? (
          <div className="gd-position-card__hint">
            {dropTarget === "titel" && dropIndex === index
              ? "Zur Titelgruppe hinzufügen"
              : "Oben/unten: einordnen · Mitte: zur Gruppe hinzufügen"}
          </div>
        ) : null}
      </button>
      <div className="gd-position-card__ep">
        {priced ? formatCurrency(positionEpNetto(pricedRow), currency) : ""}
      </div>
      <div className="gd-position-card__gesamt">
        {rabattHint ? (
          <span className="gd-position-card__gesamt-hint gd-position-card__gesamt-hint--rabatt">
            {rabattHint}
          </span>
        ) : null}
        {priced ? (
          resolvePositionsart(row) === "alternativ" ? (
            <span title="Nicht in der Gesamtsumme">
              <span className="gd-position-card__gesamt-excluded">
                {formatCurrency(totals.zeilen_netto, currency)}
              </span>
              <span className="gd-position-card__gesamt-hint">nicht in Summe</span>
            </span>
          ) : (
            formatCurrency(totals.zeilen_netto, currency)
          )
        ) : (
          ""
        )}
      </div>
      {editable && !inSelectionMode ? (
        <Button type="button" size="sm" variant="ghost" onClick={onRemove}>
          ✕
        </Button>
      ) : null}
    </div>
  );
}

function attachmentUrlsForPosition(
  docId: number | null | undefined,
  row: EditorPosition,
  attachments: GdPositionAttachmentMeta[],
): string[] {
  if (!docId || !attachments.length) return [];
  const mediaKey = (getPositionMeta(row).media_key || "").trim();
  const positionId = row.id ?? null;
  return attachments
    .filter(
      (item) =>
        (mediaKey && item.media_key === mediaKey) ||
        (positionId != null && item.position_id === positionId),
    )
    .map((item) => getGdPositionAttachmentThumbUrl(docId, item.id));
}

export function DocumentPositionCanvasPane() {
  const {
    positions,
    editable,
    currency,
    dokumentTyp,
    docId,
    positionAttachments,
    dragIndex,
    dropIndex,
    dropTarget,
    slotInsertAt,
    dragPlaceholderHeight,
    paletteOver,
    paletteDragTyp,
    paletteCopyDrag,
    displayNumbers,
    onChange,
    onOpenPosition,
    resetDrag,
    setPaletteOver,
    activateSlot,
    beginPositionDrag,
    handleInsertAt,
    handleCardDrop,
    handleCanvasDrop,
    handleCardDragOver,
    selectionMode,
    mergeExtendTargetLocalId,
    selectedLocalIds,
    contextMenu,
    startSelectionMode,
    cancelSelectionMode,
    confirmSelectionMode,
    toggleSelectedLocalId,
    openContextMenu,
    closeContextMenu,
    dissolveMerged,
    billingBadgeByLocalId,
  } = usePositionCanvasContext();

  const isLieferschein = dokumentTyp === "lieferschein";
  const isPaletteCopy = paletteCopyDrag || paletteDragTyp != null;
  const isDragActive = dragIndex != null || isPaletteCopy;

  function patchDelivery(
    localId: string,
    update: { geliefert?: number; rueckstandEntfernt?: boolean },
  ) {
    onChange(
      positions.map((p) =>
        p.localId === localId ? applyLieferscheinDelivery(p, update) : p,
      ),
    );
  }

  const selectedCount = selectedLocalIds.size;
  const canConfirmSelection =
    selectionMode === "merge"
      ? selectedCount >= 2
      : selectionMode === "merge-extend"
        ? selectedCount >= 1
        : selectionMode === "titel"
          ? selectedCount >= 1
          : false;

  function buildContextMenuItems(row: EditorPosition) {
    const items = [
      {
        id: "duplicate",
        label: "Position duplizieren",
        onClick: () => onChange(duplicatePositionInList(positions, row.localId)),
      },
    ];
    if (isMergedContainerPosition(row)) {
      items.push({
        id: "append-merge",
        label: "Unterpositionen hinzufügen",
        onClick: () => startSelectionMode("merge-extend", undefined, row.localId),
      });
      items.push({
        id: "dissolve",
        label: "Positionen auflösen",
        onClick: () => dissolveMerged(row.localId),
      });
      return items;
    }
    if (isMergeablePosition(row)) {
      items.push({
        id: "merge",
        label: "Positionen zusammenfassen",
        onClick: () => startSelectionMode("merge", row.localId),
      });
      items.push({
        id: "titel",
        label: "Positionen in einem Titel anzeigen",
        onClick: () => startSelectionMode("titel", row.localId),
      });
    }
    return items;
  }

  return (
    <>
      <section
        className={clsx(
          "gd-canvas",
          paletteOver && editable && "is-drop-target",
          isDragActive && editable && "is-dragging-active",
          selectionMode && "is-selection-mode",
        )}
        style={
          isDragActive
            ? ({
                "--gd-drop-placeholder-height": `${dragPlaceholderHeight}px`,
              } as React.CSSProperties)
            : undefined
        }
        onDragOver={(e) => {
          if (!editable || selectionMode) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = isCopySourceDrag(e, paletteDragTyp, paletteCopyDrag)
            ? "copy"
            : "move";
          setPaletteOver(true);
        }}
        onDragLeave={(e) => {
          const related = e.relatedTarget as Node | null;
          if (related && e.currentTarget.contains(related)) return;
          setPaletteOver(false);
        }}
        onDragEnd={resetDrag}
        onDrop={handleCanvasDrop}
      >
        {selectionMode ? (
          <div className="gd-position-selection-bar">
            <div>
              <p className="gd-position-selection-bar__title">
                {selectionMode === "merge"
                  ? "Positionen zum Zusammenfassen auswählen"
                  : selectionMode === "merge-extend"
                    ? "Weitere Unterpositionen auswählen"
                    : "Positionen für Titelgruppe auswählen"}
              </p>
              <p className="gd-position-selection-bar__hint">
                {selectedCount} ausgewählt · Positionen per Klick markieren
                {selectionMode === "merge-extend"
                  ? " · Bestehende Unterpositionen bleiben erhalten"
                  : ""}
              </p>
            </div>
            <div className="gd-position-selection-bar__actions">
              <Button type="button" variant="secondary" size="sm" onClick={cancelSelectionMode}>
                Abbrechen
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={!canConfirmSelection}
                onClick={confirmSelectionMode}
              >
                Bestätigen
              </Button>
            </div>
          </div>
        ) : null}
      {positions.length === 0 ? (
        <div className="gd-canvas-empty">
          {editable
            ? "Bausteine oder Vorlagen aus der rechten Spalte hierher ziehen – oder per Klick hinzufügen."
            : "Keine Positionen vorhanden."}
        </div>
      ) : (
        <>
          {positions.length ? (
            <div
              className={clsx(
                "gd-position-table-head",
                isLieferschein && "gd-position-table-head--lieferschein",
              )}
              aria-hidden
            >
              <span className="gd-position-table-head__handle" />
              <span className="gd-position-table-head__pos">Pos.</span>
              <span className="gd-position-table-head__menge">
                {isLieferschein ? "Beauf." : "Menge"}
              </span>
              {isLieferschein ? (
                <span className="gd-position-table-head__geliefert">Geliefert</span>
              ) : null}
              {isLieferschein ? (
                <span className="gd-position-table-head__rueckstand">Rückst.</span>
              ) : null}
              <span className="gd-position-table-head__bezeichnung">Bezeichnung</span>
              <span className="gd-position-table-head__ep">EP netto</span>
              <span className="gd-position-table-head__gesamt">Gesamt netto</span>
              <span className="gd-position-table-head__actions" />
            </div>
          ) : null}
          {editable ? (
            <PositionDropSlot
              insertAt={0}
              active={slotInsertAt === 0}
              isPaletteDrag={isPaletteCopy}
              dragActive={isDragActive}
              titelBoundary={isTitelGroupBoundarySlot(positions, 0)}
              placeholderHeight={dragPlaceholderHeight}
              onActivate={activateSlot}
              onDropAt={handleInsertAt}
            />
          ) : null}
          {positions.map((row, index) => {
            const typ = normalizePositionTyp(row.position_typ);
            const meta = getPositionMeta(row);
            const selectable =
              selectionMode === "merge"
                ? isMergeablePosition(row) && !isMergedContainerPosition(row)
                : selectionMode === "merge-extend"
                  ? isMergeablePosition(row) &&
                    !isMergedContainerPosition(row) &&
                    row.localId !== mergeExtendTargetLocalId
                  : selectionMode === "titel"
                    ? typ !== "titel" && typ !== "text"
                    : false;
            return (
              <div key={row.localId}>
                <PositionCard
                  row={row}
                  index={index}
                  currency={currency}
                  editable={editable}
                  positions={positions}
                  dragIndex={dragIndex}
                  dropIndex={dropIndex}
                  dropTarget={dropTarget}
                  grouped={Boolean(meta.struktur_titel_local_id)}
                  displayNumber={displayNumbers[index] ?? null}
                  groupCount={
                    typ === "titel" ? countPositionsInTitelGroup(positions, row.localId) : undefined
                  }
                  selectionMode={selectionMode}
                  selected={selectedLocalIds.has(row.localId)}
                  selectable={selectable}
                  isCopyDrag={isPaletteCopy}
                  imageUrls={attachmentUrlsForPosition(docId, row, positionAttachments)}
                  isLieferschein={isLieferschein}
                  billingBadge={billingBadgeByLocalId.get(row.localId) ?? null}
                  onDragStart={beginPositionDrag}
                  onDragEnd={resetDrag}
                  onDragOver={handleCardDragOver}
                  onDrop={handleCardDrop}
                  onOpen={() => onOpenPosition(row.localId)}
                  onToggleSelect={() => toggleSelectedLocalId(row.localId)}
                  onDeliveryChange={patchDelivery}
                  onContextMenu={(e) => {
                    if (!editable || selectionMode) return;
                    const items = buildContextMenuItems(row);
                    if (!items.length) return;
                    e.preventDefault();
                    e.stopPropagation();
                    openContextMenu({ x: e.clientX, y: e.clientY, items });
                  }}
                  onRemove={() =>
                    onChange(
                      positions
                        .filter((p) => p.localId !== row.localId)
                        .map((p, idx) => ({ ...p, sort_index: idx })),
                    )
                  }
                />
                {editable ? (
                  <PositionDropSlot
                    insertAt={index + 1}
                    active={slotInsertAt === index + 1}
                    isPaletteDrag={isPaletteCopy}
                    dragActive={isDragActive}
                    titelBoundary={isTitelGroupBoundarySlot(positions, index + 1)}
                    placeholderHeight={dragPlaceholderHeight}
                    onActivate={activateSlot}
                    onDropAt={handleInsertAt}
                  />
                ) : null}
              </div>
            );
          })}
        </>
      )}
      </section>
      <ContextMenu state={contextMenu} onClose={closeContextMenu} />
    </>
  );
}

export function DocumentPositionCanvasSidebar() {
  const {
    editable,
    dokumentTyp,
    currency,
    templates,
    templatesLoading,
    applyingTemplateId,
    templatesEnabled,
    insertBlock,
    setPaletteDragTyp,
    setPaletteCopyDrag,
    resetDrag,
    onApplyTemplate,
    onDeleteTemplate,
    onEditTemplate,
    onReorderTemplates,
    onSaveTemplate,
    auftragId,
    kundenId,
    kundeName,
    docId,
    einkaufArtikel,
    einkaufLoading,
    aufschlagProzent,
    onAufschlagChange,
    insertEinkaufArtikel,
    insertHistorischePosition,
    gebuchteStundenTotal,
    gebuchteStundenLoading,
    insertGebuchteStunden,
  } = usePositionCanvasContext();
  const [historieOpen, setHistorieOpen] = useState(false);

  if (!editable) return null;

  return (
    <>
      <DocumentBuilderSidebar
        dokumentTyp={dokumentTyp}
        templates={templates}
        templatesLoading={templatesLoading}
        applyingTemplateId={applyingTemplateId}
        templatesEnabled={templatesEnabled}
        auftragId={auftragId}
        einkaufArtikel={einkaufArtikel}
        einkaufLoading={einkaufLoading}
        aufschlagProzent={aufschlagProzent}
        onAufschlagChange={onAufschlagChange}
        gebuchteStundenTotal={gebuchteStundenTotal}
        gebuchteStundenLoading={gebuchteStundenLoading}
        onInsertBlock={insertBlock}
        onPaletteDragStart={setPaletteDragTyp}
        onPaletteDragEnd={resetDrag}
        onCopyDragStart={() => setPaletteCopyDrag(true)}
        onApplyTemplate={onApplyTemplate}
        onDeleteTemplate={onDeleteTemplate}
        onEditTemplate={onEditTemplate}
        onReorderTemplates={onReorderTemplates}
        onSaveTemplate={onSaveTemplate}
        onInsertEinkaufArtikel={insertEinkaufArtikel}
        onInsertGebuchteStunden={() => insertGebuchteStunden()}
        onOpenHistorischePositionen={() => setHistorieOpen(true)}
      />
      <DocumentHistorischePositionenDialog
        open={historieOpen}
        onClose={() => setHistorieOpen(false)}
        dokumentTyp={dokumentTyp}
        excludeDokumentId={docId}
        auftragId={auftragId}
        kundenId={kundenId}
        kundeName={kundeName}
        currency={currency}
        onInsert={insertHistorischePosition}
      />
    </>
  );
}

export function DocumentPositionCanvas({
  sidebarPlacement = "inline",
  children,
  ...props
}: DocumentPositionCanvasProps & { children?: ReactNode }) {
  const readOnlyLayout = !props.editable;
  if (sidebarPlacement === "external") {
    return (
      <DocumentPositionCanvasProvider {...props}>
        <div
          className={clsx("gd-editor-layout", readOnlyLayout && "gd-editor-layout--readonly")}
        >
          {children ?? <DocumentPositionCanvasPane />}
        </div>
      </DocumentPositionCanvasProvider>
    );
  }

  return (
    <DocumentPositionCanvasProvider {...props}>
      <div className={clsx("gd-builder", readOnlyLayout && "gd-builder--readonly")}>
        <DocumentPositionCanvasPane />
        <DocumentPositionCanvasSidebar />
      </div>
    </DocumentPositionCanvasProvider>
  );
}
